import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Plus, Trash2, Save, BookOpen, AlertCircle, Pencil } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { useKnowledgeBaseMutations } from "@/hooks/mutations/useKnowledgeBaseMutations";
import { useKnowledgeBaseQueries } from "@/hooks/useKnowledgeBaseQueries";
import { KnowledgeBaseEntry } from "@/types/theme";
import { useToast } from "@/components/ui/use-toast";

interface BuilderKnowledgeBaseProps {
  chatbotId: string;
  reportUnsavedChanges: (isDirty: boolean) => void;
}

export function BuilderKnowledgeBase({ chatbotId, reportUnsavedChanges }: BuilderKnowledgeBaseProps) {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState<KnowledgeBaseEntry | null>(null);
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const { toast } = useToast();

  const { knowledgeBase, isLoadingKnowledgeBase, error } = useKnowledgeBaseQueries(chatbotId);
  const { addKnowledgeBaseEntry, updateKnowledgeBaseEntry, deleteKnowledgeBaseEntry } = useKnowledgeBaseMutations(chatbotId);

  const handleOpenDialog = (entry?: KnowledgeBaseEntry) => {
    if (entry) {
      setEditingEntry(entry);
      setTitle(entry.title);
      setContent(entry.content);
    } else {
      setEditingEntry(null);
      setTitle("");
      setContent("");
    }
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setEditingEntry(null);
    setTitle("");
    setContent("");
    reportUnsavedChanges(false);
  };

  const handleSaveEntry = async () => {
    if (!title || !content) return; 

    const entryData = { title, content };

    try {
      if (editingEntry) {
        await updateKnowledgeBaseEntry.mutateAsync({ id: editingEntry.id, updates: entryData });
      } else {
        await addKnowledgeBaseEntry.mutateAsync(entryData);
      }
      handleCloseDialog();
    } catch (err) {
      console.error("Failed to save knowledge base entry:", err);
      toast({
        variant: "destructive",
        title: "Save Failed",
        description: "Could not save the knowledge base entry.",
      });
    }
  };

  const handleDeleteEntry = async (entryId: string) => {
    try {
      await deleteKnowledgeBaseEntry.mutateAsync(entryId);
      toast({
        title: "Entry Deleted",
        description: "The knowledge base entry has been removed.",
      });
    } catch (err) {
      console.error("Failed to delete knowledge base entry:", err);
      toast({
        variant: "destructive",
        title: "Delete Failed",
        description: "Could not remove the knowledge base entry.",
      });
    }
  };

  const handleTitleChange = (newTitle: string) => {
    setTitle(newTitle);
    if(isDialogOpen) reportUnsavedChanges(true);
  }

  const handleContentChange = (newContent: string) => {
    setContent(newContent);
    if(isDialogOpen) reportUnsavedChanges(true);
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold">Knowledge Base</h2>
          <p className="text-muted-foreground">
            Add information that will help the AI generate better responses to user queries.
          </p>
        </div>
        <Button onClick={() => handleOpenDialog()} className="flex items-center gap-2">
          <Plus size={16} /> Add Entry
        </Button>
      </div>
      
      {isLoadingKnowledgeBase ? (
        <div className="flex justify-center p-6">
          <div className="animate-spin h-6 w-6 border-2 border-primary border-t-transparent rounded-full"></div>
        </div>
      ) : error ? (
        <Card className="border-destructive">
          <CardContent className="p-6 flex items-center gap-4 text-destructive">
            <AlertCircle size={24} />
            <div>
              <h3 className="font-semibold">Error loading knowledge base</h3>
              <p>There was a problem loading your knowledge base entries. Please try refreshing the page.</p>
            </div>
          </CardContent>
        </Card>
      ) : knowledgeBase?.length === 0 ? (
        <Card className="border-dashed border-muted">
          <CardContent className="p-6 flex flex-col items-center justify-center text-center py-10 gap-4">
            <BookOpen size={48} className="text-muted-foreground opacity-50" />
            <div>
              <h3 className="font-semibold text-lg">No knowledge base entries yet</h3>
              <p className="text-muted-foreground max-w-md mx-auto mt-2">
                Add information about your products, services, or frequently asked questions to help the AI provide more accurate responses.
              </p>
            </div>
            <Button onClick={() => handleOpenDialog()} className="mt-2">
              Add your first entry
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {knowledgeBase?.map((entry) => (
            <Card key={entry.id} className="overflow-hidden">
              <CardHeader className="pb-3">
                <CardTitle className="flex justify-between items-start">
                  <span className="truncate flex-1">{entry.title}</span>
                </CardTitle>
                <CardDescription className="line-clamp-2">
                  {entry.content.substring(0, 150)}{entry.content.length > 150 ? '...' : ''}
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="flex justify-end gap-2">
                  <Button variant="outline" size="sm" onClick={() => handleOpenDialog(entry)}>
                    <Pencil size={16} className="mr-2" /> Edit
                  </Button>
                  <Button variant="destructive" size="sm" onClick={() => handleDeleteEntry(entry.id)} disabled={deleteKnowledgeBaseEntry.isPending}>
                    <Trash2 size={16} className="mr-2" /> Delete
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{editingEntry ? 'Edit Knowledge Base Entry' : 'Add Knowledge Base Entry'}</DialogTitle>
            <DialogDescription>
              Add information that will help guide AI responses to user queries.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="title">Title</Label>
              <Input
                id="title"
                value={title}
                onChange={(e) => handleTitleChange(e.target.value)}
                placeholder="e.g., Product Information, Company Policy"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="content">Content</Label>
              <Textarea
                id="content"
                value={content}
                onChange={(e) => handleContentChange(e.target.value)}
                placeholder="Add detailed information here..."
                className="min-h-[200px]"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={handleCloseDialog}>Cancel</Button>
            <Button onClick={handleSaveEntry} disabled={addKnowledgeBaseEntry.isPending || updateKnowledgeBaseEntry.isPending || !title || !content}>
              {(addKnowledgeBaseEntry.isPending || updateKnowledgeBaseEntry.isPending) ? "Saving..." : "Save Entry"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
